import { useState } from 'react';
import { Shield } from 'lucide-react';
import { getAppLogoSrc } from '../../constants/branding';
import { getBranding } from '../../config/branding';
import { currentRuntime } from '../../config/runtime';
import { resolveTenantProductName } from '../../lib/whiteLabelBranding';
import WarriorWorkspaceSymbol from './WarriorWorkspaceSymbol';
import type { WhiteLabelAppBranding } from '../../lib/whiteLabelBranding';
import { getTextLogoName } from '../../lib/textLogo';

type PulseShieldLoaderProps = {
  progress?: number | null;
  isVisible?: boolean;
  label?: string;
  sublabel?: string;
  showBrandLogo?: boolean;
  variant?: 'default' | 'workspace';
  /** Resolved tenant branding (Loading Page Logo, with primary fallback). Passes the
   *  tenant's own logo into the loader instead of the default Trade-Proof mark. */
  whiteLabel?: WhiteLabelAppBranding | null;
  /** When true on a tenant host, never fall back to the Trade-Proof logo while
   *  tenant branding is still resolving — render a neutral shield mark instead
   *  so a white-label tenant never flashes the platform brand, even for a frame. */
  neutralBrandFallback?: boolean;
  extendedLoading?: boolean;
};

export default function PulseShieldLoader({
  progress,
  isVisible = true,
  label = 'Loading Replay',
  sublabel = 'Preparing the video preview',
  showBrandLogo = true,
  variant = 'default',
  whiteLabel = null,
  neutralBrandFallback = false,
  extendedLoading = true,
}: PulseShieldLoaderProps) {
  const [logoError, setLogoError] = useState(false);
  const [whiteLabelLogoError, setWhiteLabelLogoError] = useState(false);
  const safeProgress = progress != null && Number.isFinite(progress)
    ? Math.max(0, Math.min(100, progress))
    : null;
  const isIndeterminate = safeProgress == null;
  const brandName = resolveTenantProductName();
  const logoSrc = getAppLogoSrc({ variant: 'symbol', theme: 'dark' });
  const resolvedBranding = currentRuntime.source !== 'default'
    ? getBranding(currentRuntime.brand.id)
    : null;
  const workspaceLogoSrc = resolvedBranding?.tenantId === 'warrior-trading'
    ? resolvedBranding.logos.app.symbolLight
    : resolvedBranding
      ? getAppLogoSrc({ brand: resolvedBranding, variant: 'symbol', theme: 'dark' })
      : '';

  // Workspace/replay loader branding: ALWAYS the tenant Compact / logo mark (never
  // the Primary logo). Falls back to the Trade-Proof default compact mark when the
  // tenant has no compact logo. No extra branding lookup happens here — the caller
  // passes the already-resolved branding. On a tenant host whose branding is still
  // resolving (neutralBrandFallback), suppress every brand fallback so the loader
  // shows a neutral shield mark — never the Trade-Proof logo, not even for a frame.
  const whiteLabelLogoSrc = variant === 'workspace'
    ? whiteLabel?.loadingPageLogoHref
      || whiteLabel?.compactLogoLightHref
      || whiteLabel?.compactLogoHref
      || whiteLabel?.logoHref
      || ''
    : whiteLabel?.loadingPageLogoHref || whiteLabel?.logoHref || '';
  const loadingLogoSize = whiteLabel?.loadingPageLogoSize || 'medium';
  const loadingLogoGlow = whiteLabel?.loadingPageLogoGlow !== false;
  const suppressBrandFallback = variant === 'workspace' && neutralBrandFallback && !whiteLabelLogoSrc;
  const displayLogoSrc = whiteLabelLogoSrc || (suppressBrandFallback ? '' : logoSrc);
  const effectiveWorkspaceLogoSrc = suppressBrandFallback ? '' : workspaceLogoSrc;
  const brandLogoFailed = whiteLabelLogoSrc ? whiteLabelLogoError : logoError;
  const handleBrandLogoError = () => {
    if (whiteLabelLogoSrc) setWhiteLabelLogoError(true);
    else setLogoError(true);
  };

  if (variant === 'workspace') {
    return (
      <div
        className={`absolute inset-0 z-20 flex items-center justify-center bg-[linear-gradient(180deg,#071226_0%,#08152b_52%,#061124_100%)] px-6 text-center transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        aria-hidden={!isVisible}
      >
        <div
          className={`workspace-loader__content ${extendedLoading ? 'workspace-loader__content--extended' : ''}`}
          data-loader-structure="shield-title-track-label"
        >
          <div className={`workspace-loader__shield-frame workspace-loader__shield-frame--${loadingLogoSize}`} aria-hidden="true">
            {showBrandLogo && whiteLabel?.useTextLogo ? (
              <span className={`workspace-loader__brand-text workspace-loader__brand-text--${loadingLogoSize}`} style={{ color: 'white', fontWeight: 600, lineHeight: 1.1, maxWidth: '80%', textAlign: 'center' }}>{getTextLogoName(whiteLabel.companyName)}</span>
            ) : showBrandLogo && whiteLabelLogoSrc && !whiteLabelLogoError ? (
              <img
                src={whiteLabelLogoSrc}
                alt=""
                decoding="sync"
                fetchPriority="high"
                className={`workspace-loader__brand-logo workspace-loader__brand-logo--${loadingLogoSize}`}
                data-loading-logo-size={loadingLogoSize}
                data-loading-logo-glow={loadingLogoGlow ? 'on' : 'off'}
                onError={() => setWhiteLabelLogoError(true)}
              />
            ) : !suppressBrandFallback && showBrandLogo && resolvedBranding?.tenantId === 'warrior-trading' ? (
              <WarriorWorkspaceSymbol className="workspace-loader__brand-logo workspace-loader__brand-logo--warrior" />
            ) : !suppressBrandFallback && showBrandLogo && effectiveWorkspaceLogoSrc && !logoError ? (
              <img
                src={effectiveWorkspaceLogoSrc}
                alt=""
                decoding="sync"
                fetchPriority="high"
                className={`workspace-loader__brand-logo ${
                  resolvedBranding?.tenantId === 'warrior-trading'
                    ? 'workspace-loader__brand-logo--warrior'
                    : ''
                }`}
                onError={() => setLogoError(true)}
              />
            ) : showBrandLogo && logoSrc && !logoError && !suppressBrandFallback ? (
              <img
                src={logoSrc}
                alt=""
                decoding="sync"
                fetchPriority="high"
                className="workspace-loader__brand-logo"
                onError={() => setLogoError(true)}
              />
            ) : null}
          </div>

          <div className="workspace-loader__details">
          <div className="workspace-loader__progress-display" aria-hidden="true">
            {Math.floor(safeProgress ?? 0)}%
          </div>
          <div className="workspace-loader__title">{label}</div>
          {sublabel ? <div className="workspace-loader__slow-message">{sublabel}</div> : null}

          <div
            data-testid="workspace-loading-bar"
            data-progress-mode="determinate"
            role="progressbar"
            aria-label={label}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.floor(safeProgress ?? 0)}
            className="workspace-loader__bar"
          >
            <div
              data-testid="workspace-loading-indicator"
              className="workspace-loader__bar-fill"
              style={{ width: `${safeProgress ?? 0}%` }}
              aria-hidden="true"
            />
          </div>
          <div className="workspace-loader__label">Preparing workspace</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`absolute inset-0 z-20 flex flex-col items-center justify-center rounded-[inherit] bg-[#020617]/88 backdrop-blur-md transition-all duration-700 ease-in-out ${
        isVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      aria-hidden={!isVisible}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(96,165,250,0.16),transparent_58%),linear-gradient(180deg,rgba(2,6,23,0.94)_0%,rgba(2,6,23,0.82)_100%)]" />

      <div className="relative z-10 flex w-full max-w-sm min-h-[320px] flex-col justify-between px-6 py-8 text-center select-none sm:min-h-[340px]">
        <div className="flex flex-1 items-center justify-center">
          <div className="relative z-10 flex flex-col items-center justify-center">
            <div className="relative mb-5 flex h-32 w-32 items-center justify-center sm:h-36 sm:w-36">
              <div className="absolute inset-[18%] rounded-full border border-sky-300/14 shadow-[0_0_8px_rgba(96,165,250,0.14)]" />

              {showBrandLogo && displayLogoSrc && !brandLogoFailed ? (
                <img
                  src={displayLogoSrc}
                  alt={brandName}
                  className="relative z-10 h-[6.75rem] w-[6.75rem] object-contain sm:h-[6.75rem] sm:w-[6.75rem]"
                  style={{
                    filter: 'drop-shadow(0 0 3px rgba(96, 165, 250, 0.5)) drop-shadow(0 0 8px rgba(56, 189, 248, 0.16)) brightness(1.25) contrast(1.06)',
                  }}
                  onError={handleBrandLogoError}
                />
              ) : (
                <Shield className="relative z-10 h-[6.75rem] w-[6.75rem] stroke-[1.25] text-sky-200 sm:h-[6.75rem] sm:w-[6.75rem]" />
              )}
            </div>

            {isIndeterminate ? (
              <span className="text-sm font-semibold uppercase tracking-[0.28em] text-white">
                In progress
              </span>
            ) : (
              <span className="text-5xl font-black tracking-tighter text-white tabular-nums sm:text-6xl">
                {Math.floor(safeProgress)}%
              </span>
            )}
            <span className="mt-3 text-[11px] font-semibold uppercase tracking-[0.34em] text-sky-200/90">
              {label}
            </span>
            <span className="mt-2 max-w-56 text-[12px] font-medium leading-5 text-slate-300/85">
              {sublabel}
            </span>
          </div>
        </div>

        <div className="w-full pt-6 pb-2 sm:pt-8 sm:pb-1">
          <div
            data-testid="workspace-loading-bar"
            data-progress-mode={isIndeterminate ? 'indeterminate' : 'determinate'}
            role="progressbar"
            aria-label={label}
            aria-valuemin={isIndeterminate ? undefined : 0}
            aria-valuemax={isIndeterminate ? undefined : 100}
            aria-valuenow={safeProgress == null ? undefined : Math.floor(safeProgress)}
            aria-valuetext={isIndeterminate ? 'Loading' : undefined}
            className="h-1.5 w-full overflow-hidden rounded-full bg-white/10 shadow-[0_0_18px_rgba(96,165,250,0.12)]"
          >
            <div
              className={`h-full rounded-full bg-gradient-to-r from-sky-400 via-cyan-300 to-sky-500 shadow-[0_0_14px_rgba(96,165,250,0.55)] ${
                isIndeterminate
                  ? 'mx-auto w-1/3 animate-pulse'
                  : 'transition-[width] duration-200 ease-out'
              }`}
              style={safeProgress == null ? undefined : { width: `${safeProgress}%` }}
            />
          </div>
          <div className="mt-3 text-[10px] font-semibold uppercase tracking-[0.32em] text-sky-200/70">
            Loading
          </div>
        </div>
      </div>
    </div>
  );
}
